import { Component, OnInit, inject, signal } from '@angular/core';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { firstValueFrom } from 'rxjs';
import { ApiService } from '../core/api.service';
import { AuthService } from '../core/auth.service';

@Component({
  selector: 'app-change-password',
  imports: [FormsModule, RouterLink],
  template: `
    <section class="card">
      <h1>Change Password</h1>
      @if (required()) {
        <div class="msg err">You must change your password before continuing.</div>
      }
      @if (error()) {
        <div class="msg err">{{ error() }}</div>
      }
      @if (ok()) {
        <div class="msg ok">Password updated. <a routerLink="/reservations">Continue</a></div>
      } @else {
        <label>Current password<input type="password" [(ngModel)]="currentPassword" autocomplete="current-password"></label>
        <label>New password<input type="password" [(ngModel)]="newPassword" autocomplete="new-password"></label>
        <label>Confirm new password<input type="password" [(ngModel)]="confirmPassword" autocomplete="new-password"></label>
        <div class="hint">12+ chars, at least 3 of: upper, lower, digit, symbol.</div>
        <div class="actions">
          <button type="button" (click)="submit()" [disabled]="busy()">{{ busy() ? 'Updating…' : 'Update Password' }}</button>
          <a routerLink="/reservations">Cancel</a>
        </div>
      }
    </section>
  `,
  styles: `
    .card{max-width:520px;background:#fff;border:1px solid var(--line);border-radius:16px;padding:24px;display:grid;gap:12px}
    h1{margin:0}
    label{display:grid;gap:6px;font-weight:600}
    input{height:42px;border:1px solid #c8d2e1;border-radius:10px;padding:0 12px}
    .msg{padding:10px 12px;border-radius:10px}
    .err{background:#fdeaea;color:#a40000}
    .ok{background:#eaf7ef;color:#0b5}
    .hint{font-size:12px;color:var(--muted)}
    .actions{display:flex;gap:12px;align-items:center;flex-wrap:wrap}
    button{height:40px;border:0;border-radius:10px;background:var(--primary);color:#fff;font-weight:700;padding:0 14px;cursor:pointer}
    a{color:var(--primary);text-decoration:none}
  `,
})
export class ChangePasswordComponent implements OnInit {
  private readonly api = inject(ApiService);
  private readonly auth = inject(AuthService);
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);

  readonly required = signal(false);
  readonly busy = signal(false);
  readonly error = signal('');
  readonly ok = signal(false);

  currentPassword = '';
  newPassword = '';
  confirmPassword = '';

  ngOnInit(): void {
    this.required.set(this.route.snapshot.queryParamMap.get('required') === '1');
    this.ok.set(this.route.snapshot.queryParamMap.get('changed') === '1');
  }

  async submit(): Promise<void> {
    this.error.set('');
    if (this.newPassword !== this.confirmPassword) {
      this.error.set('New passwords do not match.');
      return;
    }
    this.busy.set(true);
    try {
      await firstValueFrom(this.api.post('/auth/change-password', { currentPassword: this.currentPassword, newPassword: this.newPassword }));
      await this.auth.loadMe();
      await this.router.navigateByUrl('/account/password?changed=1');
    } catch {
      this.error.set('Unable to update password. Check your current password and policy requirements.');
    } finally {
      this.busy.set(false);
    }
  }
}
